/**
 * RoleGuard — route-level guard that enforces presence + role membership.
 *
 * Redirects unauthenticated users to ``/login`` (preserving the
 * intended path via the location state) and renders a ``403`` view
 * when the caller is authenticated but lacks the required role(s).
 */
import { Navigate, useLocation } from "react-router-dom";
import type { ReactNode } from "react";

import { useAuth, type Role } from "./AuthContext";

interface RoleGuardProps {
  allow: Role[];
  children: ReactNode;
}

export function RoleGuard({ allow, children }: RoleGuardProps): JSX.Element {
  const { user, isLoading, hasAnyRole } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <div role="status">Verificando sesión…</div>;
  }
  if (!user) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }
  if (!hasAnyRole(allow)) {
    return (
      <div role="alert">
        <h1>403 — Acceso denegado</h1>
        <p>
          Su sesión carece del rol necesario para esta vista. Roles requeridos:
          {" "}
          {allow.join(", ")}.
        </p>
      </div>
    );
  }
  return <>{children}</>;
}

export type { Role } from "./AuthContext";
